import TablesStyles from 'common/Tables/Tables.module.scss'
import { memo, useState } from 'react'
import Styles from './FileProperty.module.scss'

import { Skeleton } from 'antd'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import {
  AssignedProperty,
  DocumentPropertyValue,
  PersonPropertyValue,
  Property,
  PropertyClient,
  PropertyTag,
  PropertyValue as PropertyValueType,
} from 'api/clients/PropertyClient'
import { PropertyService } from 'api/services/PropertyService'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { PropertyValue } from 'common/PropertyValue/PropertyValue'
import { convertPropertyValuesToAssignedPropertyValues } from 'common/PropertyValue/utils'
import { useAssignedProperties } from 'hooks/useAssignedProperties'
import { useDispatch, useSelector } from 'react-redux'
import { upsertFilesProperties } from 'redux/actions/filesProperties'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { fileToAssignee } from 'screens/Finder/Utils'
import { FetchRequestState, RootState } from 'types'
import { buildErrorMessage, stopPropagation } from 'utils'
import { FilePropertyProps } from './Types'
import { useRootStore } from 'stores/RootStore'

export const createAssignedPropertyValue = (
  assignedPropertyId: number | undefined,
  property: Property,
  propertyValues: PropertyValueType[],
) => {
  const assignedPropertyValues = convertPropertyValuesToAssignedPropertyValues(property, propertyValues)
  if (!assignedPropertyValues) {
    return
  }

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return PropertyClient.updateAssignedProperty<AssignedProperty>(assignedPropertyId!, property, assignedPropertyValues)
}

const FileProperty = memo(({ file, property }: FilePropertyProps) => {
  const dispatch = useDispatch()
  const assignee = fileToAssignee(file)
  const rootStore = useRootStore()
  const assignedProperty = useSelector(({ filesProperties }: RootState) => filesProperties[file.id]?.[property.id])
  const { createAssignedProperty } = useAssignedProperties({ assignee })
  const { updatePropertyValues, getAssigneesForUpdate, refresh: refreshFinder } = useFinderContext()
  const [isEditing, setIsEditing] = useState(false)

  // The file does not have the property assigned
  if (!assignedProperty) {
    return null
  }

  if (!assignedProperty.values?.length && assignedProperty.loadingState === FetchRequestState.Loading) {
    return <Skeleton title={{ width: '100%' }} paragraph={false} active className={Styles.placeholder} />
  }

  const updatePropertyValue = async (propertyValue: PropertyValueType) => {
    const response = await PropertyClient.updatePropertyValue(file.workspace_id, propertyValue)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      const updatedPropertyValues = property.property_values.map((propertyValue) => {
        if (propertyValue.id === response.data.property_value.id) {
          return response.data.property_value
        }

        return propertyValue
      })
      updatePropertyValues(property.id, updatedPropertyValues)
      const assignees = getAssigneesForUpdate(propertyValue)

      if (!assignees) return

      const res = await PropertyClient.getBatchAssignedProperties(assignees, [property.id])

      if (res && res.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties(res.data.assigned_properties))
      }
    }
  }

  const createPropertyValue = async (propertyValue: PropertyValueType) => {
    const response = await PropertyClient.createPropertyValue(file.real_workspace_id.toString(), propertyValue)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)

    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      return response.data.property_value
    }
  }

  const onCreatePropertyValue = async (propertyValueData: PropertyValueType) => {
    let newAssignedProperty: AssignedProperty | undefined

    if (property.property_tag !== PropertyTag.PostAt) {
      newAssignedProperty = assignedProperty.id ? assignedProperty : await createAssignedProperty(property)
    }

    const propertyValue = await createPropertyValue(propertyValueData)

    if (propertyValue) {
      const updatedPropertyValues = [...property.property_values, propertyValue]
      updatePropertyValues(property.id, updatedPropertyValues)

      if (property.property_tag !== PropertyTag.PostAt && newAssignedProperty) {
        const payload =
          property.type === 'multi_select' ? [...(assignedProperty.values || []), propertyValue] : [propertyValue]
        const res = await createAssignedPropertyValue(newAssignedProperty.id, property, payload)
        if (res && res.type === SUCCESS_RESPONSE_TYPE) {
          dispatch(upsertFilesProperties([res.data.assigned_property]))
        }
      }
    }
  }

  const onDeletePropertyValue = async (propertyValue: PropertyValueType) => {
    const response = await PropertyService.deletePropertyValue(file.workspace_id, property, propertyValue)
    handleErrorResponseWithMessage(buildErrorMessage(response.data.errors))(response)
    if (response && response.type === SUCCESS_RESPONSE_TYPE) {
      const updatedPropertyValues = property.property_values.filter((pV) => pV.id !== propertyValue.id)
      updatePropertyValues(property.id, updatedPropertyValues)
      const assignees = getAssigneesForUpdate(propertyValue)

      if (!assignees) return

      const res = await PropertyClient.getBatchAssignedProperties(assignees, [property.id])

      if (res && res.type === SUCCESS_RESPONSE_TYPE) {
        dispatch(upsertFilesProperties(res.data.assigned_properties))
      }
    }
  }

  const selectValue = async (propertyValues: PropertyValueType[] | DocumentPropertyValue[] | PersonPropertyValue[]) => {
    if (!assignedProperty.id && !propertyValues.length) return

    const newAssignedProperty = assignedProperty.id ? assignedProperty : await createAssignedProperty(property)

    const res = await createAssignedPropertyValue(newAssignedProperty.id, property, propertyValues)
    if (res) {
      handleErrorResponseWithMessage(buildErrorMessage(res.data.errors))(res)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        if (res.data.assigned_property?.property?.property_tag === 'channel') {
          refreshFinder()
          rootStore.files.fetch(file.id, true)
        }
        dispatch(upsertFilesProperties([res.data.assigned_property]))
      }
    }
  }

  return (
    <div className={Styles.propertyValue} onClick={stopPropagation}>
      <PropertyValue
        inline
        assignee={assignee}
        property={property}
        selectedPropertyValues={assignedProperty?.values || []}
        onSelect={selectValue}
        onUpdate={updatePropertyValue}
        onCreate={onCreatePropertyValue}
        onDelete={onDeletePropertyValue}
        valueClassName={TablesStyles.columnText}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        data-testid="property-value"
      />
    </div>
  )
})
FileProperty.displayName = 'FileProperty'

export { FileProperty }
